import * as os from 'node:os';
import * as path from 'node:path';
import * as portfinder from 'portfinder';
import * as fs from 'node:fs';
import * as childProcess from 'node:child_process';
import * as utils from '../utils';
import * as servicesApi from './testimServicesApi';
import { getLogger } from './logger';
import type { RunnerOptions, TunnelOptions } from 'src/runOptions';

const logger = getLogger('testimCloudflare');

// Remember to update the checksum when the version is updated (TUNNEL_BINARY_PATHNAME)
const TUNNEL_BINARY_ORIGIN = 'https://github.com/cloudflare/cloudflared/releases/download/2025.11.1/';

// Platform dependent binary file paths and associated SHA256 checksums for version 2025.11.1
// Remember to update the checksum when the version is updated (TUNNEL_BINARY_PATHNAME)
const TUNNEL_BINARY_PATHNAME: Partial<{ [os: string]: { path: string; sha256: string; extract?: true } }> = {
    win32ia32: { path: 'cloudflared-windows-386.exe', sha256: '1e76c0d4e6ff2576db46ad1a7397415729fbaf886b22aa49327f66185a2e8fc8' },
    win32x64: { path: 'cloudflared-windows-amd64.exe', sha256: '413f9b24dc6e61a455564651524f167b8ce29ac4ccd40703dea7af93cd37ed39' },
    darwinx64: { path: 'cloudflared-darwin-amd64.tgz', sha256: '155a288fef19dba08f0c7145c16a207baf137462d8a1289a78bf8564f9e51244', extract: true },
    darwinarm64: { path: 'cloudflared-darwin-arm64.tgz', sha256: '45cfbb59a720f60b873906aa6469f8c4058f26be6d351c3e2920bc9cb4714273', extract: true },
    linuxia32: { path: 'cloudflared-linux-386', sha256: 'bec7a150d54d5c9e950ed41b434678f302698ad4638693ac9166ad186e4bb945' },
    linuxx64: { path: 'cloudflared-linux-amd64', sha256: '991dffd8889ee9f0147b6b48933da9e4407e68ea8c6d984f55fa2d3db4bb431d' },
};

const TUNNEL_BINARY_DIRECTORY = os.tmpdir();
const TUNNEL_BINARY_NAME = os.platform() === 'win32' ? 'cloudflared.exe' : 'cloudflared';
const TUNNEL_BINARY_LOCATION = path.join(TUNNEL_BINARY_DIRECTORY, TUNNEL_BINARY_NAME);
const TUNNEL_CONNECTION_TIMEOUT_MS = 60000;
const TUNNEL_POLL_INTERVAL_MS = 500;

let tunnelId: string | null | undefined = null;
let tunnelProcess: childProcess.ChildProcess | null = null;


// Polls the /metrics endpoint until ha_connections >= 1, to ensure the tunnel is connected.
const waitForTunnelConnection = async (proc: childProcess.ChildProcess, metricsPort: number): Promise<void> => {
    const metricsUrl = `http://localhost:${metricsPort}/metrics`;

    const processError = new Promise<never>((_, reject) => {
        proc.on('error', (err) => {
            const msg = `Cloudflare tunnel process error: ${err.message}`;
            logger.error(msg, { error: err });
            reject(new Error(msg));
        });
        proc.on('exit', (code) => {
            if (code !== 0) {
                const msg = `Cloudflare tunnel exited with code ${code}`;
                logger.error(msg, { code });
                reject(new Error(msg));
            }
        });
    });

    const pollMetrics = async (): Promise<void> => {
        // The while(true) is used to keep polling the metrics endpoint until the tunnel is connected or the timeout in timeoutPromise is reached.
        while (true) {
            try {
                const response = await fetch(metricsUrl);
                const metrics = await response.text();
                // Get the number of ha_connections from the metrics response
                const match = metrics.match(/cloudflared_tunnel_ha_connections\s+(\d+)/);
                if (match && Number(match[1]) >= 1) {
                    return;
                }
            } catch {
                // Metrics endpoint not ready yet
            }
            await utils.delay(TUNNEL_POLL_INTERVAL_MS);
        }
    };

    const timeoutPromise = utils.delay(TUNNEL_CONNECTION_TIMEOUT_MS).then(() => {
        const msg = `Cloudflare tunnel connection timeout after ${TUNNEL_CONNECTION_TIMEOUT_MS} seconds`;
        logger.error(msg, { timeout: TUNNEL_CONNECTION_TIMEOUT_MS });
        throw new Error(msg);
    });

    await Promise.race([pollMetrics(), processError, timeoutPromise]);
};

export const prepareTunnel = async () => {
    const isBinaryExist = await utils.doesPathExist(TUNNEL_BINARY_LOCATION);
    if (isBinaryExist) {
        return;
    }

    const downloadUrl = TUNNEL_BINARY_PATHNAME[os.platform() + os.arch()];
    if (!downloadUrl) {
        const msg = `tunnel on ${os.platform() + os.arch()} platform is not supported.`;
        logger.error(msg, { platform: os.platform() + os.arch() });
        throw new Error(msg);
    }
    const destination = downloadUrl.extract ? path.join(TUNNEL_BINARY_DIRECTORY, downloadUrl.path) : TUNNEL_BINARY_LOCATION;
    await utils.downloadAndSave(`${TUNNEL_BINARY_ORIGIN}/${downloadUrl.path}`, destination);
    await utils.verifyChecksum(destination, downloadUrl.sha256);
    if (downloadUrl.extract) {
        await utils.extractTgz(destination, TUNNEL_BINARY_DIRECTORY);
    }
    await fs.promises.chmod(TUNNEL_BINARY_LOCATION, '755');
};

export const connectTunnel = async (options: Partial<TunnelOptions> | RunnerOptions) => {
    const tunnelBaseUrl = (options as RunnerOptions).baseUrl || `http://localhost:${options.tunnelPort || 80}`;
    const tunnelRoutes = ('tunnelRoutes' in options && options.tunnelRoutes) || [tunnelBaseUrl];
    const [result] = await Promise.all([
        servicesApi.getCloudflareTunnel(options.company!.companyId, tunnelRoutes),
        prepareTunnel(),
    ]);
    tunnelId = result._id;

    // The port being brought from portfinder is NOT random, it is sequential starting from 9000.
    const availablePort = await portfinder.getPortPromise({ port: 9000 });
    tunnelProcess = childProcess.spawn(
        TUNNEL_BINARY_LOCATION,
        ['--metrics', `localhost:${availablePort}`, 'tunnel', '--no-autoupdate', 'run', '--protocol', 'http2', '--token', result.token!],
        { stdio: ['ignore', 'pipe', 'pipe'] }
    );

    await waitForTunnelConnection(tunnelProcess, availablePort);
    await servicesApi.forceUpdateCloudflareTunnelRoutes(options.company!.companyId, tunnelId);

    if ('tunnelRoutesOutput' in options && options.tunnelRoutesOutput) {
        await fs.promises.writeFile(options.tunnelRoutesOutput, JSON.stringify(result.routesMapping, null, 2));
    }

    (options as RunnerOptions).baseUrl = `${options.tunnelUseHttpAddress ? 'http' : 'https'}://${result.routesMapping![tunnelBaseUrl as keyof typeof result.routesMapping]}`;
};

export const disconnectTunnel = async (options: Partial<TunnelOptions> | RunnerOptions) => {
    const promises = [];
    if (tunnelId) {
        promises.push(servicesApi.deleteCloudflareTunnel(options.company!.companyId, tunnelId));
    }
    if (tunnelProcess) {
        promises.push(new Promise<void>((resolve, reject) => {
            tunnelProcess!.on('close', (code) => {
                if (code) {
                    reject(new Error(`tunnel process exited with code ${code}`));
                }
                resolve();
            });
            tunnelProcess!.kill();
        }));
    }
    return await Promise.all(promises);
};
